/**
 * `useCanEditResource` / `useCanEditResourceFor` — matrix-driven role
 * decision hooks.
 *
 * **SB2-CC-01 (this revision) — matrix-driven, single source of truth.**
 * The old hand-rolled `MUTATE_ROLES` table was the bug Christian
 * demonstrated at [00:29:31]–[00:29:58] of the 2026-05-20 walkthrough
 * (canonical `platform_admin` user, in both Zitadel and `auth_db`,
 * blocked from Edit Site because the FE table didn't list
 * `platform_admin` in the site mutate-set). The fix is to consume the
 * BE's authoritative role × permission matrix from
 * `GET /api/auth/v1/role-matrix` — the same matrix the BE `RoleGuard`
 * enforces.
 *
 * Hook contract:
 *   - `useCanEditResource(permission)` — returns `true` when the matrix
 *     cell for `(currentRole, permission)` is unconditionally `true`;
 *     returns `false` otherwise (including for `"conditional"` cells —
 *     callers needing the conditional predicate use
 *     `useCanEditResourceFor`).
 *   - `useCanEditResourceFor(permission, resource)` — additionally
 *     resolves `"conditional"` cells against a lightweight client-side
 *     predicate (currently: tenant-scope match). The BE remains the
 *     security boundary; this hook is a UX nicety that prevents a
 *     flash-of-editable-UI on a row the BE will 403 on Save.
 *
 * Loading state: while the matrix is still being fetched (or no
 * `RoleMatrixContext.Provider` is present in the tree) BOTH hooks
 * return `false`. This gives consumers a safe-default-deny state — a
 * read-only modal renders until the matrix lands, never a flash of
 * editable UI on a row the user can't actually mutate.
 *
 * Legacy compat: `canEditResource(roles, resource)` — the original
 * two-argument call signature is kept for the small set of test/spec
 * files that still import it. Deprecated; new call sites use the
 * hooks. The legacy table is intentionally NOT consulted at runtime
 * by `useCanEditResource`: the only source-of-truth is the matrix.
 */
import {
  useRoleMatrixContext,
  type PermissionKey as ContextPermissionKey,
} from "../auth/RoleMatrixContext.js";

// Re-export so app code can keep `import { PermissionKey } from
// "@aeriosiq/ui"` without reaching into the auth subfolder.
export type PermissionKey = ContextPermissionKey;
export type {
  Role,
  RoleMatrix,
  RoleMatrixCell,
} from "../auth/RoleMatrixContext.js";

/**
 * The full role set in the Aero Enterprise tenant per
 * `_BACKEND/.vibe/58_ROLE_MANAGEMENT.md` §1 (canonical seven). Kept as
 * a named type for the small set of callers that need the union value
 * (e.g. test fixtures, the legacy `canEditResource` shim).
 */
export type UserRole =
  | "platform_admin"
  | "tenant_admin"
  | "project_manager"
  | "back_office"
  | "pre_screener"
  | "expert_analyst"
  | "pilot";

/**
 * Legacy resource-kind union — used only by the deprecated
 * `canEditResource(roles, resource)` shim below. New code uses
 * `PermissionKey` instead.
 *
 * @deprecated Use `PermissionKey` (e.g. `"site.update"`, `"turbine.update"`).
 */
export type ResourceKind =
  | "site"
  | "turbine"
  | "turbine_model"
  | "component"
  | "component_model"
  | "data_acquisition";

/**
 * Matrix-driven role decision. Returns `true` iff the matrix cell for
 * `(currentRole, permission)` is the unconditional boolean `true`.
 * Returns `false` for unconditional `false` AND for `"conditional"`
 * cells (use `useCanEditResourceFor` to resolve the conditional
 * predicate).
 *
 * Returns `false` while the matrix is loading or absent — see file
 * header for the safe-default rationale.
 */
export function useCanEditResource(permission: PermissionKey): boolean {
  const { role, matrix } = useRoleMatrixContext();
  if (!role || !matrix) return false; // safe default while loading
  const rowKey = role as string;
  const row = matrix.grid[rowKey];
  if (!row) return false;
  const cell = row[permission];
  if (cell === true) return true;
  if (cell === false) return false;
  // `"conditional"` — caller should use useCanEditResourceFor.
  return false;
}

/**
 * Matrix-driven role decision WITH a client-side predicate for the
 * `"conditional"` cells. The predicate mirrors the BE's
 * `role-predicates.ts` for the within-tenant gates — the canonical
 * majority of `"conditional"` cells (~80 of 87 per the
 * `role-matrix.spec.json` `conditions:` map). For
 * `platform_admin.<permission> = "conditional"` cells (cross-tenant
 * superuser cases) the FE optimistically returns `true` and lets the
 * BE enforce the actual cross-tenant audit-log requirement.
 *
 * The BE is the security boundary. This hook is a UX nicety: it stops
 * the user from typing into a field the BE will 403 on Save. A
 * server-side refusal still happens regardless of what this hook
 * returns.
 */
export function useCanEditResourceFor(
  permission: PermissionKey,
  resource: { tenantId?: string | null; tenantSlug?: string | null } = {},
): boolean {
  const { role, tenantSlug, matrix } = useRoleMatrixContext();
  if (!role || !matrix) return false; // safe default while loading
  const rowKey = role as string;
  const row = matrix.grid[rowKey];
  if (!row) return false;
  const cell = row[permission];
  if (cell === true) return true;
  if (cell === false) return false;
  // `"conditional"` — apply the FE-side optimistic predicate.
  // 1. `platform_admin` is the cross-tenant superuser. Every
  //    `"conditional"` cell for platform_admin is a cross-tenant
  //    audit-log case; the BE permits them all. UI-side, return true.
  if (role === "platform_admin") return true;
  // 2. Within-tenant predicate. If the resource carries a tenant
  //    identifier, compare against the caller's. If it does not
  //    (legacy callers that pre-date the tenant-on-resource shape),
  //    assume same-tenant — the BE remains authoritative.
  const resourceTenant = resource.tenantSlug ?? resource.tenantId ?? null;
  if (resourceTenant === null) return true;
  if (tenantSlug === null) return false;
  // Tenant match → optimistically allow; BE re-verifies on the wire.
  return resourceTenant === tenantSlug;
}

// ---------------------------------------------------------------------------
// Legacy compat — `canEditResource(roles, resource)` and the
// `MUTATE_ROLES` table. Kept ONLY for the test/spec call sites that
// still consume the pre-CC-01 signature. Production call sites have
// moved to `useCanEditResource(permission)` / `useCanEditResourceFor`.
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-deprecated --
   The `ResourceKind` references below are inside the legacy shim
   itself; the lint rule is intended for callers, not for the shim's
   own internals. Production code uses `PermissionKey`. */

const LEGACY_MUTATE_ROLES: Record<ResourceKind, ReadonlySet<UserRole>> = {
  site: new Set<UserRole>([
    "platform_admin",
    "tenant_admin",
    "project_manager",
    "back_office",
  ]),
  turbine: new Set<UserRole>([
    "platform_admin",
    "tenant_admin",
    "project_manager",
    "back_office",
  ]),
  turbine_model: new Set<UserRole>([
    "platform_admin",
    "tenant_admin",
    "project_manager",
    "back_office",
  ]),
  component: new Set<UserRole>([
    "platform_admin",
    "tenant_admin",
    "project_manager",
    "back_office",
  ]),
  component_model: new Set<UserRole>([
    "platform_admin",
    "tenant_admin",
    "project_manager",
    "back_office",
  ]),
  data_acquisition: new Set<UserRole>([
    "platform_admin",
    "tenant_admin",
    "project_manager",
    "back_office",
    "pre_screener",
    "expert_analyst",
  ]),
};

/**
 * @deprecated Use `useCanEditResourceFor(permission, resource)`. The
 * non-hook form exists for legacy non-React callers (selectors,
 * test fixtures) that still consume the `(roles, resource)` shape.
 *
 * NOTE: in keeping with SB2-CC-01 the legacy table now INCLUDES
 * `platform_admin` in every mutate set — the bug Christian
 * demonstrated at [00:29:31] of the 2026-05-20 walkthrough was
 * exactly this row missing.
 */
export function canEditResource(
  roles: readonly string[],
  resource: ResourceKind,
): boolean {
  const allowed = LEGACY_MUTATE_ROLES[resource];
  for (const r of roles) {
    if (allowed.has(r as UserRole)) return true;
  }
  return false;
}
/* eslint-enable @typescript-eslint/no-deprecated */
